import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  launchesApi,
  tasksApi,
  taskTypesApi,
  usersApi,
} from "../../api/endpoints";
import { ApiError } from "../../api/client";
import { ModalBackdrop } from "../../components/ModalBackdrop";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import {
  ConflictDialog,
  type ConflictFieldDiff,
} from "../../components/ConflictDialog";
import {
  useCanWriteTasks,
  useTaskPermissions,
} from "../../hooks/useTaskPermissions";
import { useSession } from "../../hooks/useSession";
import { IcoCheck, IcoClose, IcoEdit, IcoTrash } from "../../components/icons";
import { isTouchLike } from "../../lib/device";
import type {
  Task,
  TaskComment,
  TaskReactionEmoji,
  TaskRiskLevel,
  TaskStatus,
  WorkspaceUser,
} from "../../types/domain";

const STATUS_OPTIONS: Array<{ id: TaskStatus; label: string }> = [
  { id: "planned", label: "Запланировано" },
  { id: "in_progress", label: "В работе" },
  { id: "done", label: "Готово" },
  { id: "cancelled", label: "Отменено" },
];

const RISK_OPTIONS: Array<{ id: TaskRiskLevel; label: string }> = [
  { id: "none", label: "Нет риска" },
  { id: "risk", label: "Риск" },
  { id: "critical", label: "Критично" },
];

const WEEKDAY_OPTIONS = [
  { id: 1, label: "Пн" },
  { id: 2, label: "Вт" },
  { id: 3, label: "Ср" },
  { id: 4, label: "Чт" },
  { id: 5, label: "Пт" },
  { id: 6, label: "Сб" },
  { id: 0, label: "Вс" },
];

const COMMENT_REACTIONS: Array<{ id: TaskReactionEmoji; label: string; title: string }> = [
  { id: "thumbs_up", label: "👍", title: "Нравится" },
  { id: "heart", label: "❤️", title: "Сердце" },
  { id: "eyes", label: "👀", title: "Смотрю" },
  { id: "party", label: "🎉", title: "Класс" },
  { id: "check", label: "✅", title: "Принято" },
];

interface CreateSeed {
  /** День, в который кладём задачу на доске. */
  plannedDate?: string | null;
  launchId?: string | null;
}

interface Props {
  mode: "create" | "edit";
  task?: Task;
  seed?: CreateSeed;
  onClose: () => void;
}

/** Состояние диалога конфликта. Хранит свежую серверную копию задачи,
 *  чтобы пользователь мог либо «забрать сервер», либо force-сохранить своё. */
type ConflictState =
  | { open: false }
  | { open: true; serverTask: Task };

export function TaskModal({ mode, task, seed, onClose }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useSession().data ?? null;
  // Роль в scope=own (встроенный staff или кастомная роль с tasks.viewOwn без
  // полного view) — задача всегда «своя», ответственный фиксируется на
  // текущего пользователя и переназначить нельзя. Зеркало серверного
  // isTaskOwnScope.
  const ownScope =
    me?.permissions?.tasks?.viewOwn === true &&
    me?.permissions?.tasks?.view !== true;
  const isAdmin = me?.permissions?.admin?.manageUsers === true;
  const canWriteTasks = useCanWriteTasks();
  const perms = useTaskPermissions(task);
  // На create — нужно общее право работать с задачами (tasks.create).
  // На edit — конкретное право над этой задачей.
  const canSubmit = mode === "create" ? canWriteTasks : perms.canEdit;
  const readOnly = !canSubmit;
  // «Только-исполнитель» (scope=own с assigneeId === me, но без createdById === me):
  // постановщицкие поля заблокированы, можно менять только status и description.
  // На create — этот режим не активен (scope=own создаёт сам себе).
  const executionOnly = mode === "edit" && perms.canEdit && !perms.canEditAll;
  // Scope=own не может переназначать ответственного даже на своей задаче.
  const assigneeLocked = ownScope;

  const taskTypesQuery = useQuery({
    queryKey: ["taskTypes"],
    queryFn: async () => (await taskTypesApi.list()).types,
    staleTime: 60_000,
  });
  const taskTypes = taskTypesQuery.data ?? [];

  const usersQuery = useQuery({
    queryKey: ["users", "workspace"],
    queryFn: async () => (await usersApi.list()).users,
    staleTime: 60_000,
  });
  const users = usersQuery.data ?? [];

  const [title, setTitle] = useState(task?.title ?? "");
  const [description, setDescription] = useState(task?.description ?? "");
  const [assigneeId, setAssigneeId] = useState<string>(task?.assigneeId ?? "");
  const [plannedDate, setPlannedDate] = useState<string>(
    task?.plannedDate ?? seed?.plannedDate ?? "",
  );
  // На create дедлайн по умолчанию = «Запланировано на» (seed.plannedDate).
  // Без задачи на руках обе даты совпадают; менеджер при необходимости
  // правит крайний срок руками. На edit берём что было в задаче.
  const [dueDate, setDueDate] = useState<string>(
    task?.dueDate ?? seed?.plannedDate ?? "",
  );
  const [dueTime, setDueTime] = useState<string>(task?.dueTime ?? "");
  const [status, setStatus] = useState<TaskStatus>(task?.status ?? "planned");
  const [riskLevel, setRiskLevel] = useState<TaskRiskLevel>(task?.riskLevel ?? "none");
  const [taskTypeId, setTaskTypeId] = useState<string>(
    task?.taskTypeId ?? "",
  );
  const [launchId, setLaunchId] = useState<string>(task?.launchId ?? seed?.launchId ?? "");
  const initialRepeatDay = weekdayFromIso(task?.plannedDate ?? seed?.plannedDate ?? "");
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [repeatWeekDays, setRepeatWeekDays] = useState<number[]>(
    initialRepeatDay === null ? [] : [initialRepeatDay],
  );
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [conflict, setConflict] = useState<ConflictState>({ open: false });

  // Baseline для optimistic-lock. Захвачен при открытии модалки и НЕ
  // обновляется по ходу редактирования — это та версия, которую видел
  // пользователь. Сервер сравнивает её с актуальным updatedAt и кидает 409,
  // если за это время прилетела чужая правка.
  const [baselineUpdatedAt] = useState<string | null>(task?.updatedAt ?? null);

  // На create-форме первый загруженный тип (по sortOrder) становится default'ом.
  const effectiveTypeId =
    taskTypeId ||
    (mode === "create" && taskTypes.length > 0 ? taskTypes[0]!.id : "");

  const launchesQuery = useQuery({
    queryKey: ["launches"],
    queryFn: async () => (await launchesApi.list()).launches,
  });
  const launches = launchesQuery.data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["tasks"] });

  // Если человек поставил только дедлайн, не указав день в плане —
  // план = дедлайн (задача попадает в колонку дедлайна, а не «Без даты»).
  const effectivePlanned = plannedDate || dueDate || null;

  /** Пакуем тело PATCH/POST из локального стейта формы. Изолировано, чтобы
   *  force-сохранение в конфликте не дублировало сборку.
   *  assigneeId передаём только когда он реально изменился — иначе при
   *  редактировании задачи с legacy-assigneeName (вписанным вручную в старой
   *  версии) можно случайно затереть имя при первом же save. */
  const buildPayload = (): TaskPayload => {
    const initialAssignee = task?.assigneeId ?? "";
    const assigneeChanged = assigneeId !== initialAssignee;

    // Для assignee-only staff отправляем только status/description — иначе
    // сервер вернёт 403 staff_field_locked. Это и UI-блокировка, и сетевая
    // защита от случайных изменений (вдруг state остался от прошлой версии).
    if (executionOnly) {
      return {
        status,
        description: description.trim() || null,
      } as TaskPayload;
    }

    const base: TaskPayload = {
      title: title.trim(),
      description: description.trim() || null,
      plannedDate: mode === "create" ? effectivePlanned : plannedDate || null,
      dueDate: dueDate || null,
      dueTime: dueDate ? dueTime || null : null,
      status,
      riskLevel,
      taskTypeId: effectiveTypeId || undefined,
      launchId: launchId || null,
    };
    if (ownScope) {
      // Scope=own не управляет ответственным: на create сервер автоматом ставит
      // self, на edit поле игнорируется. Не передаём, чтобы не словить 403.
    } else if (mode === "create") {
      if (assigneeId) base.assigneeId = assigneeId;
    } else if (assigneeChanged) {
      base.assigneeId = assigneeId || null;
    }
    if (mode === "create" && repeatWeekly) {
      const fallbackDay = weekdayFromIso(effectivePlanned ?? "");
      const days =
        repeatWeekDays.length > 0
          ? repeatWeekDays
          : fallbackDay === null
          ? []
          : [fallbackDay];
      base.recurrence = { frequency: "weekly", weekDays: days };
    }
    return base;
  };

  const createMutation = useMutation({
    mutationFn: () => tasksApi.create(buildPayload()),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  /** Общий PATCH с optimistic-lock. force=true — игнорируем baseline
   *  (вторая попытка после 409). */
  const runPatch = async (force: boolean) => {
    if (!task) throw new Error("no task");
    const opts =
      !force && baselineUpdatedAt
        ? { expectedUpdatedAt: baselineUpdatedAt }
        : undefined;
    return tasksApi.patch(task.id, buildPayload(), opts);
  };

  const patchMutation = useMutation({
    mutationFn: ({ force }: { force: boolean }) => runPatch(force),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.status === 409 && err.code === "conflict") {
        const current = (err.details as { current?: Task } | undefined)?.current;
        if (current) {
          setConflict({ open: true, serverTask: current });
          setError(null);
          return;
        }
      }
      setError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (scope: "single" | "future") => {
      if (!task) throw new Error("no task");
      return tasksApi.remove(task.id, { scope });
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  const canSave = title.trim().length > 0 && !!effectiveTypeId;
  const isPending =
    createMutation.isPending || patchMutation.isPending || deleteMutation.isPending;
  const canComment = mode === "edit" && perms.canEdit;

  const onSave = () => {
    if (readOnly || !canSave || isPending) return;
    if (mode === "create" && repeatWeekly && !effectivePlanned) {
      setError("Для повторения нужна дата в поле «Запланировано на» или «Крайний срок».");
      return;
    }
    setError(null);
    if (mode === "create") createMutation.mutate();
    else patchMutation.mutate({ force: false });
  };

  const onConflictTakeTheirs = () => {
    if (!conflict.open) return;
    invalidate();
    onClose();
  };

  const onConflictKeepMine = () => {
    patchMutation.mutate({ force: true });
    setConflict({ open: false });
  };

  const titleText =
    readOnly
      ? "Просмотр задачи"
      : mode === "create"
      ? "Новая задача"
      : "Задача";

  return (
    <>
      <ModalBackdrop onClose={onClose}>
        <div className="modal task-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-head">
            <h2>{titleText}</h2>
            <div className="spacer" />
            <button type="button" className="modal-close" onClick={onClose}>
              <IcoClose size={16} />
            </button>
          </div>
          <div className="modal-body task-modal-body">
            <div className="task-modal-title-row">
              <input
                className="form-input form-input--title task-modal-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus={!readOnly}
                disabled={readOnly || executionOnly}
                placeholder="Название задачи"
              />
              <select
                className="form-input task-modal-type-select"
                value={effectiveTypeId}
                onChange={(e) => setTaskTypeId(e.target.value)}
                disabled={readOnly || executionOnly}
                title="Тип задачи"
              >
                {taskTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="task-modal-row">
              <FieldDate
                label="Запланировано на"
                value={plannedDate}
                onChange={setPlannedDate}
                disabled={readOnly || executionOnly}
              />
              <FieldDateTime
                label="Крайний срок"
                dateValue={dueDate}
                timeValue={dueTime}
                onDateChange={setDueDate}
                onTimeChange={setDueTime}
                disabled={readOnly || executionOnly}
              />
            </div>
            {isTouchLike() && !readOnly && !executionOnly && (
              <div className="task-modal-hint">
                Чтобы перенести задачу в другую колонку — измените «Запланировано на».
              </div>
            )}
            {mode === "create" && !readOnly && !executionOnly && (
              <div className="task-repeat-box">
                <label className="task-repeat-toggle">
                  <input
                    type="checkbox"
                    checked={repeatWeekly}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setRepeatWeekly(checked);
                      if (checked && repeatWeekDays.length === 0) {
                        const day = weekdayFromIso(effectivePlanned ?? "");
                        if (day !== null) setRepeatWeekDays([day]);
                      }
                    }}
                  />
                  <span>Повторять каждую неделю</span>
                </label>
                {repeatWeekly && (
                  <div className="task-repeat-days" aria-label="Дни повторения">
                    {WEEKDAY_OPTIONS.map((day) => {
                      const active = repeatWeekDays.includes(day.id);
                      return (
                        <button
                          key={day.id}
                          type="button"
                          className={"task-repeat-day" + (active ? " is-active" : "")}
                          onClick={() =>
                            setRepeatWeekDays((prev) =>
                              prev.includes(day.id)
                                ? prev.filter((x) => x !== day.id)
                                : [...prev, day.id],
                            )
                          }
                        >
                          {day.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="task-modal-row">
              <FieldAssignee
                value={assigneeId}
                onChange={setAssigneeId}
                users={users}
                loading={usersQuery.isLoading}
                disabled={readOnly || assigneeLocked || executionOnly}
                lockedToSelfHint={
                  assigneeLocked
                    ? mode === "create"
                      ? "Создаётся на вас"
                      : "Изменение ответственного недоступно для роли «Сотрудник»"
                    : undefined
                }
              />
              <FieldSelect<string>
                value={launchId}
                onChange={setLaunchId}
                disabled={readOnly || executionOnly}
                options={[
                  { value: "", label: "— без запуска —" },
                  ...launches.map((l) => ({ value: l.id, label: l.name })),
                ]}
              />
            </div>

            <div className="task-modal-row">
              <FieldSelect<TaskStatus>
                value={status}
                onChange={(v) => setStatus(v)}
                disabled={readOnly}
                options={STATUS_OPTIONS.map((o) => ({ value: o.id, label: o.label }))}
              />
              <FieldSelect<TaskRiskLevel>
                value={riskLevel}
                onChange={(v) => setRiskLevel(v)}
                disabled={readOnly || executionOnly}
                options={RISK_OPTIONS.map((o) => ({ value: o.id, label: o.label }))}
              />
            </div>

            <textarea
              className="form-input task-modal-description-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={12}
              disabled={readOnly}
              placeholder="Описание: детали, ссылки, контекст"
            />

            {mode === "edit" && task && (
              <TaskCommentsPanel
                task={task}
                users={users}
                currentUserId={me?.id ?? null}
                canComment={canComment}
              />
            )}

            {error && (
              <div style={{ color: "var(--text-danger, #DC2626)", fontSize: 12.5 }}>{error}</div>
            )}
          </div>
          <div className="modal-foot">
            {mode === "edit" && perms.canDelete && (
              <button
                type="button"
                className="btn-ghost btn-danger-ghost"
                onClick={() => setConfirmDelete(true)}
                disabled={isPending}
              >
                <IcoTrash size={14} /> Удалить
              </button>
            )}
            {mode === "edit" && isAdmin && task && (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  onClose();
                  navigate(`/admin/tasks-audit?taskId=${encodeURIComponent(task.id)}`);
                }}
                disabled={isPending}
                title="История изменений по этой задаче"
              >
                История
              </button>
            )}
            <div className="spacer" />
            <button type="button" className="btn-ghost" onClick={onClose}>
              {readOnly ? "Закрыть" : "Отмена"}
            </button>
            {!readOnly && (
              <button
                type="button"
                className="btn-primary"
                onClick={onSave}
                disabled={!canSave || isPending}
              >
                <IcoCheck size={14} /> {mode === "create" ? "Создать" : "Сохранить"}
              </button>
            )}
          </div>
        </div>
      </ModalBackdrop>

      {confirmDelete && (
        <ConfirmDialog
          title={task?.recurrenceId ? "Удалить повторяющуюся задачу?" : "Удалить задачу?"}
          message={
            task?.recurrenceId
              ? "Это экземпляр повторяющейся задачи. Можно удалить только этот день или остановить серию с этой даты."
              : "Действие нельзя отменить. Задача будет удалена для всех."
          }
          body={
            task?.recurrenceId ? (
              <button
                type="button"
                className="btn-ghost btn-danger-ghost task-delete-future-btn"
                onClick={() => {
                  deleteMutation.mutate("future");
                  setConfirmDelete(false);
                }}
                disabled={deleteMutation.isPending}
              >
                Удалить этот и все будущие дни
              </button>
            ) : null
          }
          confirmLabel={task?.recurrenceId ? "Удалить только этот день" : "Удалить задачу"}
          busy={deleteMutation.isPending}
          onConfirm={() => {
            deleteMutation.mutate("single");
            setConfirmDelete(false);
          }}
          onClose={() => setConfirmDelete(false)}
        />
      )}

      {conflict.open && task && (
        <ConflictDialog
          title="Задача изменена другим пользователем"
          updatedAt={conflict.serverTask.updatedAt}
          fields={diffTaskFields(buildPayload(), conflict.serverTask)}
          onKeepMine={onConflictKeepMine}
          onTakeTheirs={onConflictTakeTheirs}
          onClose={() => setConflict({ open: false })}
          busy={patchMutation.isPending}
        />
      )}
    </>
  );
}

function TaskCommentsPanel({
  task,
  users,
  currentUserId,
  canComment,
}: {
  task: Task;
  users: WorkspaceUser[];
  currentUserId: string | null;
  canComment: boolean;
}) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");
  const [editingMentionIds, setEditingMentionIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["taskComments", task.id],
    queryFn: async () => (await tasksApi.comments.list(task.id)).comments,
    staleTime: 10_000,
  });
  const comments = query.data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["taskComments", task.id] });

  const createMutation = useMutation({
    mutationFn: () =>
      tasksApi.comments.create(task.id, body.trim(), activeMentionIds(body, mentionIds, users)),
    onSuccess: () => {
      setBody("");
      setMentionIds([]);
      setError(null);
      invalidate();
    },
    onError: (err: Error) => setError(err.message),
  });

  const patchMutation = useMutation({
    mutationFn: (vars: { id: string; body: string }) =>
      tasksApi.comments.patch(
        task.id,
        vars.id,
        vars.body,
        activeMentionIds(vars.body, editingMentionIds, users),
      ),
    onSuccess: () => {
      setEditingId(null);
      setEditingBody("");
      setError(null);
      invalidate();
    },
    onError: (err: Error) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => tasksApi.comments.remove(task.id, id),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err: Error) => setError(err.message),
  });
  const reactionMutation = useMutation({
    mutationFn: (vars: { comment: TaskComment; emoji: TaskReactionEmoji }) => {
      const state = vars.comment.reactions.find((r) => r.emoji === vars.emoji);
      return state?.reactedByMe
        ? tasksApi.comments.removeReaction(task.id, vars.comment.id, vars.emoji)
        : tasksApi.comments.addReaction(task.id, vars.comment.id, vars.emoji);
    },
    onSuccess: () => invalidate(),
    onError: (err: Error) => setError(err.message),
  });

  const busy =
    createMutation.isPending ||
    patchMutation.isPending ||
    deleteMutation.isPending ||
    reactionMutation.isPending;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canComment || busy || !body.trim()) return;
    createMutation.mutate();
  };

  return (
    <section className="task-comments">
      <div className="task-comments-head">
        <h3>Комментарии</h3>
        <span>{comments.length}</span>
      </div>

      {query.isLoading ? (
        <div className="task-comments-empty">Загружаем комментарии…</div>
      ) : comments.length === 0 ? (
        <div className="task-comments-empty">Пока нет обсуждения по этой задаче.</div>
      ) : (
        <div className="task-comments-list">
          {comments.map((comment) => (
            <TaskCommentItem
              key={comment.id}
              comment={comment}
              currentUserId={currentUserId}
              editingId={editingId}
              editingBody={editingBody}
              editingMentionIds={editingMentionIds}
              busy={busy}
              onStartEdit={() => {
                setEditingId(comment.id);
                setEditingBody(comment.body);
                setEditingMentionIds(comment.mentions.map((m) => m.id));
              }}
              onCancelEdit={() => {
                setEditingId(null);
                setEditingBody("");
                setEditingMentionIds([]);
              }}
              onEditBody={setEditingBody}
              onEditMentions={setEditingMentionIds}
              onSaveEdit={() => {
                if (!editingBody.trim()) return;
                patchMutation.mutate({ id: comment.id, body: editingBody.trim() });
              }}
              onDelete={() => deleteMutation.mutate(comment.id)}
              onToggleReaction={(emoji) => reactionMutation.mutate({ comment, emoji })}
              users={users}
            />
          ))}
        </div>
      )}

      {canComment && (
        <form className="task-comment-form" onSubmit={onSubmit}>
          <MentionTextarea
            value={body}
            onChange={setBody}
            mentionIds={mentionIds}
            onMentionIdsChange={setMentionIds}
            users={users}
            currentUserId={currentUserId}
            disabled={busy}
            placeholder="Написать комментарий, можно @упомянуть сотрудника"
          />
          <div className="task-comment-form-foot">
            {error && <span className="task-comment-error">{error}</span>}
            <button type="submit" className="btn-primary" disabled={busy || !body.trim()}>
              Отправить
            </button>
          </div>
        </form>
      )}
      {!canComment && (
        <div className="task-comments-empty">
          Обсуждение доступно только участникам с правом редактирования задачи.
        </div>
      )}
    </section>
  );
}

function TaskCommentItem({
  comment,
  currentUserId,
  editingId,
  editingBody,
  editingMentionIds,
  busy,
  users,
  onStartEdit,
  onCancelEdit,
  onEditBody,
  onEditMentions,
  onSaveEdit,
  onDelete,
  onToggleReaction,
}: {
  comment: TaskComment;
  currentUserId: string | null;
  editingId: string | null;
  editingBody: string;
  editingMentionIds: string[];
  busy: boolean;
  users: WorkspaceUser[];
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onEditBody: (body: string) => void;
  onEditMentions: (ids: string[]) => void;
  onSaveEdit: () => void;
  onDelete: () => void;
  onToggleReaction: (emoji: TaskReactionEmoji) => void;
}) {
  const isOwn = !!currentUserId && comment.author?.id === currentUserId;
  const isEditing = editingId === comment.id;
  const deleted = !!comment.deletedAt;
  const authorName = comment.author?.name?.trim() || comment.author?.email || "Удалённый пользователь";

  return (
    <article className={"task-comment" + (deleted ? " is-deleted" : "")}>
      <div className="task-comment-meta">
        <span className="task-comment-author">{authorName}</span>
        <span>{formatCommentTime(comment.createdAt)}</span>
        {!deleted && comment.updatedAt !== comment.createdAt && <span>изменено</span>}
      </div>

      {deleted ? (
        <div className="task-comment-deleted">Комментарий удалён</div>
      ) : isEditing ? (
        <div className="task-comment-edit">
          <MentionTextarea
            value={editingBody}
            onChange={onEditBody}
            mentionIds={editingMentionIds}
            onMentionIdsChange={onEditMentions}
            users={users}
            currentUserId={currentUserId}
            disabled={busy}
            placeholder="Комментарий"
          />
          <div className="task-comment-actions">
            <button type="button" className="btn-ghost" onClick={onCancelEdit} disabled={busy}>
              Отмена
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={onSaveEdit}
              disabled={busy || !editingBody.trim()}
            >
              Сохранить
            </button>
          </div>
        </div>
      ) : (
        <>
          <p>{renderCommentBody(comment.body, comment.mentions)}</p>
          <div className="task-comment-actions">
            <div className="task-comment-reactions" aria-label="Реакции">
              {COMMENT_REACTIONS.map((reaction) => {
                const state = comment.reactions.find((r) => r.emoji === reaction.id);
                return (
                  <button
                    key={reaction.id}
                    type="button"
                    className={"task-reaction-btn" + (state?.reactedByMe ? " is-active" : "")}
                    onClick={() => onToggleReaction(reaction.id)}
                    disabled={busy}
                    title={reaction.title}
                    aria-label={reaction.title}
                  >
                    <span>{reaction.label}</span>
                    {state?.count ? <b>{state.count}</b> : null}
                  </button>
                );
              })}
            </div>
            {isOwn && (
              <div className="task-comment-tools">
                <button
                  type="button"
                  className="task-comment-icon-btn"
                  onClick={onStartEdit}
                  disabled={busy}
                  title="Изменить"
                  aria-label="Изменить"
                >
                  <IcoEdit size={13} />
                </button>
                <button
                  type="button"
                  className="task-comment-icon-btn is-danger"
                  onClick={onDelete}
                  disabled={busy}
                  title="Удалить"
                  aria-label="Удалить"
                >
                  <IcoTrash size={13} />
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </article>
  );
}

function MentionTextarea({
  value,
  onChange,
  mentionIds,
  onMentionIdsChange,
  users,
  currentUserId,
  disabled,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  mentionIds: string[];
  onMentionIdsChange: (ids: string[]) => void;
  users: WorkspaceUser[];
  currentUserId: string | null;
  disabled: boolean;
  placeholder: string;
}) {
  const query = currentMentionQuery(value);
  const candidates =
    query === null
      ? []
      : users
          .filter((u) => u.id !== currentUserId)
          .filter((u) => userMentionLabel(u).toLowerCase().includes(query.toLowerCase()))
          .slice(0, 6);

  const addMention = (user: WorkspaceUser) => {
    const q = currentMentionQuery(value);
    if (q === null) return;
    const at = value.lastIndexOf(`@${q}`);
    const before = at >= 0 ? value.slice(0, at) : value;
    const after = at >= 0 ? value.slice(at + q.length + 1) : "";
    const next = `${before}@${userMentionLabel(user)} ${after}`.replace(/\s+$/g, " ");
    onChange(next);
    if (!mentionIds.includes(user.id)) onMentionIdsChange([...mentionIds, user.id]);
  };

  return (
    <div className="mention-input-wrap">
      <textarea
        className="form-input task-comment-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        disabled={disabled}
        placeholder={placeholder}
      />
      {candidates.length > 0 && (
        <div className="mention-menu">
          {candidates.map((u) => (
            <button key={u.id} type="button" onClick={() => addMention(u)}>
              <span className="mention-avatar">{userMentionLabel(u).slice(0, 1).toUpperCase()}</span>
              <span>{userMentionLabel(u)}</span>
              <small>{u.email}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function currentMentionQuery(value: string): string | null {
  const match = /(^|\s)@([^\s@]{0,40})$/.exec(value);
  return match ? match[2] ?? "" : null;
}

function userMentionLabel(user: { name: string | null; email: string }): string {
  return user.name?.trim() || user.email.split("@")[0] || user.email;
}

function activeMentionIds(body: string, ids: string[], users: WorkspaceUser[]): string[] {
  return ids.filter((id) => {
    const user = users.find((u) => u.id === id);
    if (!user) return false;
    return body.includes(`@${userMentionLabel(user)}`);
  });
}

function renderCommentBody(
  body: string,
  mentions: TaskComment["mentions"],
): ReactNode[] | string {
  if (mentions.length === 0) return body;
  const labels = mentions.map((m) => `@${userMentionLabel(m)}`).sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`(${labels.map(escapeRegex).join("|")})`, "g");
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of body.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > last) nodes.push(body.slice(last, start));
    nodes.push(
      <span key={`${start}-${match[0]}`} className="task-comment-mention">
        {match[0]}
      </span>,
    );
    last = start + match[0].length;
  }
  if (last < body.length) nodes.push(body.slice(last));
  return nodes;
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function formatCommentTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface TaskPayload {
  title: string;
  description: string | null;
  plannedDate: string | null;
  dueDate: string | null;
  dueTime: string | null;
  status: TaskStatus;
  riskLevel: TaskRiskLevel;
  taskTypeId: string | undefined;
  launchId: string | null;
  assigneeId?: string | null;
  recurrence?: { frequency: "weekly"; weekDays: number[] } | null;
}

function diffTaskFields(mine: TaskPayload, theirs: Task): ConflictFieldDiff[] {
  const fields: Array<[label: string, mineV: string, theirsV: string]> = [
    ["Название", mine.title ?? "", theirs.title ?? ""],
    ["Описание", mine.description ?? "", theirs.description ?? ""],
    ["Статус", String(mine.status ?? ""), String(theirs.status ?? "")],
    ["Риск", String(mine.riskLevel ?? ""), String(theirs.riskLevel ?? "")],
    ["Запланировано на", mine.plannedDate ?? "", theirs.plannedDate ?? ""],
    ["Крайний срок", mine.dueDate ?? "", theirs.dueDate ?? ""],
    ["Время крайнего срока", mine.dueTime ?? "", theirs.dueTime ?? ""],
    ["Ответственный", mine.assigneeId ?? "", theirs.assigneeId ?? ""],
    ["Запуск", mine.launchId ?? "", theirs.launchId ?? ""],
  ];
  return fields
    .filter(([, a, b]) => a !== b)
    .map(([label, yours, theirsV]) => ({ label, yours, theirs: theirsV }));
}

function weekdayFromIso(iso: string): number | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return d.getDay();
}

function FieldDate({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <input
      type="date"
      className="form-input task-modal-field"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      title={label}
      aria-label={label}
    />
  );
}

function FieldDateTime({
  label,
  dateValue,
  timeValue,
  onDateChange,
  onTimeChange,
  disabled,
}: {
  label: string;
  dateValue: string;
  timeValue: string;
  onDateChange: (v: string) => void;
  onTimeChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="task-modal-field task-due-input-row">
      <input
        type="date"
        className="form-input"
        value={dateValue}
        onChange={(e) => onDateChange(e.target.value)}
        disabled={disabled}
        title={label}
        aria-label={label}
      />
      <input
        type="time"
        className="form-input task-due-time-input"
        value={timeValue}
        onChange={(e) => onTimeChange(e.target.value)}
        disabled={disabled || !dateValue}
        placeholder="--:--"
        aria-label={`${label}: время`}
      />
    </div>
  );
}

function FieldAssignee({
  value,
  onChange,
  users,
  loading,
  disabled,
  lockedToSelfHint,
}: {
  value: string;
  onChange: (v: string) => void;
  users: Array<{ id: string; email: string; name: string | null }>;
  loading: boolean;
  disabled?: boolean;
  lockedToSelfHint?: string;
}) {
  const sorted = [...users].sort((a, b) => {
    const an = (a.name || a.email).toLowerCase();
    const bn = (b.name || b.email).toLowerCase();
    return an.localeCompare(bn);
  });
  return (
    <select
      className="form-input task-modal-field"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || loading}
      title={lockedToSelfHint ?? "Ответственный"}
      aria-label="Ответственный"
    >
      <option value="">{lockedToSelfHint ?? "— ответственный —"}</option>
      {sorted.map((u) => (
        <option key={u.id} value={u.id}>
          {u.name?.trim() ? u.name : u.email}
        </option>
      ))}
    </select>
  );
}

function FieldSelect<T extends string>({
  value,
  onChange,
  options,
  disabled,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
  disabled?: boolean;
}) {
  return (
    <select
      className="form-input task-modal-field"
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      disabled={disabled}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
